import React, { ComponentType, ReactNode, useEffect, useMemo } from 'react';
import { useIsAuthenticated } from '@azure/msal-react';
import { Navigate } from 'react-router-dom';

import {
  CropConnectRoleDefinitions,
  RoleAssignmentDto,
  RoleAssignmentType,
} from '../cropConnectApiClient';
import { useContextStore } from '../../stores/storeRoot';

export interface SecurityTrimProps {
  requiredGlobalRoles?: CropConnectRoleDefinitions[];
  requiredBusinessPartnerRoles?: CropConnectRoleDefinitions[];
  onlyNordzuckerInternalUsers?: boolean;
  /**Idicates how the evaluation should be done. AND will be true if the requiredRoles + requiredBusinessPartnerRoles + onlyNorduckerInternalUsers
   * conditions are met. With OR any of these must be met
   */
  securityTrimMode?: SecurityTrimMode;
  /**If the flag is set the component won't ever be displayed */
  disableAccess?: boolean;
  /**If the flag is set, then during AOB the globalRoles together with the securityTrimMode will also be considered.
   * By default this flag should be off, which means only the businessPartnerRoles will be considered during AOB */
  overrideGrowerRolesDuringAOB?: boolean;
}

export enum SecurityTrimMode {
  AND,
  OR,
}

interface SecurityTrimControlProps extends SecurityTrimProps {
  children: ReactNode;
  fallback?: ReactNode;
}

interface FilteredPermissions {
  globalRoles: CropConnectRoleDefinitions[];
  bpScopedRoles: CropConnectRoleDefinitions[];
}

const filterPermissions = (
  globalAssignment: RoleAssignmentDto | undefined,
  businessPartnerAssignment: RoleAssignmentDto | undefined
): FilteredPermissions => {
  return {
    globalRoles: globalAssignment?.roles ?? [],
    bpScopedRoles: businessPartnerAssignment?.roles ?? [],
  };
};

const validateRoleAssingmentType = (
  assingment: RoleAssignmentDto,
  expectedType: RoleAssignmentType
) => {
  return assingment.type == expectedType;
};

/**The default method for evaluating security trim conditions.
 * Can be an alternative in code when the SecurityTrim component cannot be used. */
export const securityTrimConditionsMet = (
  securityProperties: SecurityTrimProps,
  globalAssignment: RoleAssignmentDto | undefined,
  businessPartnerAssignment: RoleAssignmentDto | undefined,
  nordzuckerInternalUser: boolean,
  isAccessOnBehalf?: boolean
) => {
  if (securityProperties.disableAccess) {
    return false;
  }
  if (
    globalAssignment &&
    !validateRoleAssingmentType(globalAssignment, RoleAssignmentType.Global)
  ) {
    throw new Error('Assignment is not of type Global');
  }
  if (
    businessPartnerAssignment &&
    !validateRoleAssingmentType(
      businessPartnerAssignment,
      RoleAssignmentType.BusinessPartner
    )
  ) {
    throw new Error('Assignment is not of type BusinessPartner');
  }
  const calculatedUserPermissions = filterPermissions(
    globalAssignment,
    businessPartnerAssignment
  );
  const globalPermissionsSatisfied =
    securityProperties.requiredGlobalRoles == undefined ||
    (Array.isArray(securityProperties.requiredGlobalRoles) &&
      securityProperties.requiredGlobalRoles.length == 0)
      ? true
      : calculatedUserPermissions.globalRoles.some((r) =>
          securityProperties.requiredGlobalRoles?.includes(r)
        );
  const bpPermissionsSatisfied =
    securityProperties.requiredBusinessPartnerRoles == undefined ||
    (Array.isArray(securityProperties.requiredBusinessPartnerRoles) &&
      securityProperties.requiredBusinessPartnerRoles.length == 0)
      ? true
      : calculatedUserPermissions.bpScopedRoles.some((r) =>
          securityProperties.requiredBusinessPartnerRoles?.includes(r)
        );
  const internalUserPermissionSatisfied =
    securityProperties.onlyNordzuckerInternalUsers == undefined ||
    securityProperties.onlyNordzuckerInternalUsers == false
      ? true
      : nordzuckerInternalUser ==
        securityProperties.onlyNordzuckerInternalUsers;

  if (!securityProperties.overrideGrowerRolesDuringAOB) {
    if (isAccessOnBehalf) {
      return bpPermissionsSatisfied;
    }
  }

  if (securityProperties.securityTrimMode === SecurityTrimMode.OR) {
    const rolesPermissionsSatisfied =
      globalPermissionsSatisfied || bpPermissionsSatisfied;
    if (securityProperties.onlyNordzuckerInternalUsers == undefined) {
      return rolesPermissionsSatisfied;
    }
    return internalUserPermissionSatisfied || rolesPermissionsSatisfied;
  } else {
    return (
      internalUserPermissionSatisfied &&
      globalPermissionsSatisfied &&
      bpPermissionsSatisfied
    );
  }
};

const SecurityTrim: React.FC<SecurityTrimControlProps> = (
  props: SecurityTrimControlProps
) => {
  const {
    businessPartnerAssignment,
    globalAssignment,
    activeBpNumber,
    nordzuckerInternalUser,
    isAccessOnBehalf,
  } = useContextStore();
  const isAuthenticated = useIsAuthenticated();

  const shouldDisplay = useMemo(() => {
    return (
      isAuthenticated &&
      securityTrimConditionsMet(
        props,
        globalAssignment,
        businessPartnerAssignment,
        nordzuckerInternalUser,
        isAccessOnBehalf
      )
    );
  }, [
    activeBpNumber,
    businessPartnerAssignment,
    globalAssignment,
    nordzuckerInternalUser,
  ]);

  return <>{shouldDisplay ? props.children : props.fallback}</>;
};

export const withSecurityTrim = <P extends object>(
  WrappedComponent: ComponentType<P>,
  options: SecurityTrimProps
): React.FC<P> => {
  const componentWithSecurityTrim: React.FC<P> = (props) => {
    return (
      <SecurityTrim {...options}>
        <WrappedComponent {...props} />
      </SecurityTrim>
    );
  };
  return componentWithSecurityTrim;
};

//There is no direct way in React Router (needs to be wrapped in Route)
//Example usage
/* 
 <Route
    path={myFieldsYieldsPath}
    element={
      <SecurityTrimmedRoute
        securityProperties={{
          requiredBusinessPartnerRoles: [
            CropConnectRoleDefinitions.Shop,
          ],
        }}
        fallbackPath={rootPath}
      >
        <MyFieldsYields />
      </SecurityTrimmedRoute>
    }
  />
*/
export const SecurityTrimmedRoute: React.FC<{
  children?: ReactNode | undefined;
  securityProperties: SecurityTrimProps;
  fallbackPath: string;
}> = ({ children, securityProperties, fallbackPath }) => {
  const {
    activeBpNumber,
    globalAssignment,
    businessPartnerAssignment,
    nordzuckerInternalUser,
  } = useContextStore();

  useEffect(() => {}, [activeBpNumber]);

  if (
    securityTrimConditionsMet(
      securityProperties,
      globalAssignment,
      businessPartnerAssignment,
      nordzuckerInternalUser
    )
  ) {
    return children;
  }

  return <Navigate to={fallbackPath} replace />;
};

export default SecurityTrim;
